// composables/usePdfDesigner.js
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import Swal from "sweetalert2";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";

GlobalWorkerOptions.workerSrc = "/js/pdf.worker.min.mjs";

export function usePdfDesigner(options = {}) {
    const {
        resourceApi = "doctors",
        resourceId = null,
        showRecord,
        editModel,
        defaultLabels = [],
        localStorageKey = "pdfData",
        pdfEntyKey = "doctor",
        pdfParametersKey = "prescription_pdf_parameters",
        pdfUrlKey = "pdf_url",
        generatePdfMethod = null,
        notify = () => { }, // Ejemplo de notificación
        filterLabelsForSave = null, // Función para filtrar etiquetas
        userData = null, // Datos del usuario desde el store
        apiService = null, // Servicio de API
        updateUserStore = null, // Función para actualizar el store de usuario
    } = options;

    // Para acceder al proxy si usas $notify o similares

    // Refs y states principales
    const pdfContainer = ref(null);
    const pdfCanvas = ref(null);

    // Guardaremos la información completa que venga del backend
    const dataEntity = ref(null);

    // URL final del PDF que mostraremos
    const pdfData = ref({
        url: "",
        labels: [],
    });

    // Para arrastrar/soltar archivo
    const selectedFile = ref(null);

    // Control para evitar renderizados concurrentes
    const isRendering = ref(false);
    let currentRenderTask = null;
    let currentPageRenderTask = null; // Para controlar el renderizado de la página específicamente

    // Estado para la escala visual del contenedor
    const visualScale = ref(1);
    const containerWidth = ref(861); // Ancho original del canvas
    const containerHeight = ref(1115); // Alto original del canvas

    /**
     * --------------------------------------------------------------------------------
     * 1. Carga del recurso desde la API (genérico para cualquier entidad)
     * --------------------------------------------------------------------------------
     */
    const getResource = async () => {

        // Si tenemos datos del usuario directamente, los usamos
        if (userData && userData.value && userData.value.doctor) {
            dataEntity.value = userData.value;
            if (userData.value.doctor[pdfUrlKey]) {
                pdfData.value.url = userData.value.doctor[pdfUrlKey];
            } else {
                // Intentar buscar con nombres alternativos
                const alternativeKeys = ['prescription_pdf_url', 'pdf_url', 'prescription_pdf'];
                for (const key of alternativeKeys) {
                    if (userData.value.doctor[key]) {
                        pdfData.value.url = userData.value.doctor[key];
                        break;
                    }
                }
            }

            // Extrae las etiquetas guardadas en DB (si existen)
            const paramsData = userData.value.doctor[pdfParametersKey];
            if (paramsData) {
                try {
                    // El backend puede enviar el dato como string JSON o como objeto ya parseado
                    const params = typeof paramsData === 'string' ? JSON.parse(paramsData) : paramsData;
                    if (params && params.labels && params.labels.length > 0) {

                        // Si no hay etiquetas en pdfData, inicializar con array vacío
                        if (!pdfData.value.labels) {
                            pdfData.value.labels = [];
                        }

                        // Reemplazar completamente las etiquetas con las del servidor
                        pdfData.value.labels = params.labels.map((label, index) => ({
                            id: label.id ?? `server-${index}`,
                            text: label.text || "",
                            x: label.x || 0,
                            y: label.y || 0,
                            page: label.page || 1,
                            isInput: label.isInput || false,
                            width: label.width || 100,
                            height: label.height || 20,
                            value: label.value || "",
                        }));

                        // IMPORTANTE: Actualizar localStorage con los datos del servidor
                        // Esto asegura que localStorage tenga los datos más recientes
                        savePdfData();
                    } else {
                        // Si no hay etiquetas válidas desde el servidor, array vacío
                        console.warn('⚠️ No hay etiquetas válidas desde el servidor');
                        pdfData.value.labels = [];
                    }
                } catch (error) {
                    // En caso de error de parseo, array vacío
                    console.error('❌ Error al parsear prescription_pdf_parameters:', error);
                    pdfData.value.labels = [];
                }
            } else {
                // Si no hay parámetros en el servidor, array vacío
                console.warn('⚠️ No existe prescription_pdf_parameters en el servidor');
                pdfData.value.labels = [];
            }

            await renderPDF();
            return;
        }

        // Método original si no tenemos userData
        if (!showRecord || !resourceId) return;
        dataEntity.value = await showRecord({ id: resourceId }, resourceApi);
        if (!dataEntity.value) return;

        // Extrae la URL del PDF (si existe)
        if (dataEntity.value[pdfUrlKey]) {
            pdfData.value.url = dataEntity.value[pdfUrlKey];
        }

        // Extrae las etiquetas guardadas en DB (si existen)
        const params = dataEntity.value?.[pdfEntyKey]?.[pdfParametersKey]
            ? JSON.parse(dataEntity.value[pdfEntyKey][pdfParametersKey])
            : null;

        if (params && params.labels) {
            // Mezclamos las etiquetas del server con las que ya tengamos
            params.labels.forEach((label, index) => {
                const existingLabel = pdfData.value.labels.find((l) => l.id === label.id);
                if (existingLabel) {
                    existingLabel.x = label.x;
                    existingLabel.y = label.y;
                    existingLabel.width = label.width;
                    existingLabel.height = label.height;
                    existingLabel.value = label.value;
                } else {
                    pdfData.value.labels.push({
                        ...label,
                        id: label.id ?? `generated-${index}`,
                        value: label.value || "",
                    });
                }
            });
        }

        renderPDF();
    };

    /**
     * --------------------------------------------------------------------------------
     * 2. Carga/guardado de posiciones en localStorage
     * --------------------------------------------------------------------------------
     */
    const loadPdfData = () => {
        const savedData = localStorage.getItem(localStorageKey);
        if (savedData) {
            const storedLabels = JSON.parse(savedData);
            storedLabels.forEach((label, index) => {
                const existingLabel = pdfData.value.labels.find((l) => l.id === label.id);
                if (existingLabel) {
                    existingLabel.x = label.x;
                    existingLabel.y = label.y;
                    existingLabel.width = label.width;
                    existingLabel.height = label.height;
                } else {
                    pdfData.value.labels.push({
                        ...label,
                        id: label.id ?? `generated-${index}`,
                        value: label.value || "",
                    });
                }
            });
            // NO llamar savePdfData() aquí - solo cargar, no sobrescribir
        } else {
            console.log("⚠️ No hay datos en localStorage");
        }
    };

    const savePdfData = () => {
        localStorage.setItem(localStorageKey, JSON.stringify(pdfData.value.labels));
    };

    const loadDefaultPdfData = () => {
        localStorage.removeItem(localStorageKey);
        pdfData.value.labels = [];

        // Carga los labels por defecto
        pdfData.value.labels = defaultLabels.map((label, index) => ({
            id: label.id ?? `default-${index}`,
            text: label.text || "",
            x: label.x || 0,
            y: label.y || 0,
            page: label.page || 1,
            isInput: label.isInput || false,
            width: label.width || 100,
            height: label.height || 20,
            value: label.value || "",
        }));

        savePdfData();
        renderPDF();
    };

    /**
     * --------------------------------------------------------------------------------
     * 3. Subir PDF nuevo al servidor
     * --------------------------------------------------------------------------------
     */
    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            selectedFile.value = file;
            sendPdfUpload(file);
        }
    };

    const uploadPDF = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            notify("Por favor, seleccione un archivo PDF.", "warning");
            return;
        }
        sendPdfUpload(file);
    };

    const sendPdfUpload = async (file) => {
        try {

            const formData = new FormData();
            formData.append("pdf", file);

            // Render inmediato con URL local para evitar ver el PDF anterior
            const tempUrl = URL.createObjectURL(file);
            pdfData.value.url = tempUrl;
            if (pdfCanvas.value) {
                await renderPDF();
            }

            // Usar apiService si está disponible, sino usar fakeUploadPdf
            let response;
            if (apiService) {
                const endpoint = `${resourceApi}/${resourceId}/uploadpdf`;
                response = await apiService.post(endpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                response = await fakeUploadPdf(resourceApi, resourceId, formData);
            }

            if (response && (response.status === 200 || response.status === 201)) {
                // Buscar la URL del PDF en múltiples ubicaciones posibles
                const pdfUrl = response.data.prescription_pdf_url ||
                    response.prescription_pdf_url ||
                    response.data.pdf_url ||
                    response.pdf_url ||
                    response.data.data?.prescription_pdf_url;


                if (pdfUrl) {
                    pdfData.value.url = pdfUrl;

                    // Revocar el blob previo para liberar memoria
                    if (tempUrl) {
                        URL.revokeObjectURL(tempUrl);
                    }

                    await nextTick();
                    if (pdfCanvas.value) {
                        await renderPDF();
                    }

                    // Actualizar el store de usuario con los nuevos datos del doctor
                    if (updateUserStore && response.data.data) {
                        const doctorData = response.data.data;

                        // Asegurar que la URL del PDF esté en el lugar correcto
                        const updatedDoctor = {
                            ...doctorData,
                            prescription_pdf_url: pdfUrl, // URL desde la respuesta
                            [pdfUrlKey]: pdfUrl // También guardar con la clave configurada
                        };

                        updateUserStore({
                            doctor: updatedDoctor
                        });
                    }

                    // Si hay parámetros en la respuesta, cargarlos
                    if (response.data.data && response.data.data.prescription_pdf_parameters) {
                        try {
                            const paramsData = response.data.data.prescription_pdf_parameters;
                            const params = typeof paramsData === 'string' ? JSON.parse(paramsData) : paramsData;
                            if (params && params.labels) {
                                pdfData.value.labels = params.labels.map((label, index) => ({
                                    id: label.id ?? `server-${index}`,
                                    text: label.text || "",
                                    x: label.x || 0,
                                    y: label.y || 0,
                                    page: label.page || 1,
                                    isInput: label.isInput || false,
                                    width: label.width || 100,
                                    height: label.height || 20,
                                    value: label.value || "",
                                }));
                            }
                        } catch (parseError) {
                            console.error("Error al parsear parámetros del PDF:", parseError);
                        }
                    }

                    // Asegurar que el DOM se actualice antes de renderizar
                    await nextTick();

                    // Verificar que el canvas esté disponible
                    if (!pdfCanvas.value) {
                        notify("Error: Canvas no disponible para renderizar el PDF", "danger");
                        return;
                    }

                    await renderPDF();
                    notify(response.data.message || "PDF subido correctamente", "success");
                } else {
                    // Si el backend no devuelve URL, mantenemos el preview local
                    notify("No se recibió la URL del PDF; mostrando vista previa local", "warning");
                }
            } else {
                notify("Error al subir el PDF", "danger");
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                notify(error.response.data.message, "danger");
            } else {
                notify("Ocurrió un error al subir el archivo PDF.", "danger");
            }
        }
    };

    // (Ejemplo FALSO de servicio simulando la subida al backend)
    const fakeUploadPdf = (apiName, id, formData) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    status: 200,
                    data: {
                        pdf_url: URL.createObjectURL(formData.get("pdf")),
                        message: "PDF subido correctamente (mock).",
                    },
                });
            }, 1000);
        });
    };

    /**
     * --------------------------------------------------------------------------------
     * 4. Renderizar el PDF en un <canvas>
     * --------------------------------------------------------------------------------
     */
    const resolvePdfUrl = (url) => {
        if (!url) return null;
        const trimmed = String(url).trim();

        // URLs absolutas o blobs ya son válidas
        if (/^(https?:|blob:|data:)/i.test(trimmed)) {
            return trimmed;
        }

        try {
            // Usar la base del apiService si existe (asegura mismo dominio que la API)
            const rawBase =
                (apiService && apiService.defaults && apiService.defaults.baseURL) ||
                process.env.VUE_APP_API_BASE_URL ||
                window.location.origin;

            // Si el baseURL trae path (/api), usar solo el origin para rutas absolutas del host
            let baseForRelative = rawBase;
            try {
                const parsed = new URL(rawBase);
                baseForRelative = parsed.origin;
            } catch (_) {
                baseForRelative = window.location.origin;
            }

            // Permitir rutas relativas del backend (/storage/...)
            return new URL(trimmed, baseForRelative).toString();
        } catch (err) {
            console.error("URL de PDF inválida:", trimmed, err);
            return null;
        }
    };

    const shouldSendCredentials = (targetUrl) => {
        if (!targetUrl) return false;
        try {
            const parsedTarget = new URL(targetUrl, window.location.origin);
            const apiBase = apiService?.defaults?.baseURL;
            const parsedApi = apiBase ? new URL(apiBase) : null;
            const matchesApiHost = parsedApi ? parsedApi.host === parsedTarget.host : false;
            const matchesAppHost = parsedTarget.origin === window.location.origin;
            return matchesApiHost || matchesAppHost;
        } catch (err) {
            console.warn("No se pudo determinar si se necesitan credenciales para el PDF:", err);
            return false;
        }
    };

    const fetchPdfAsUint8 = async (url, useCredentials = false) => {
        try {
            const token = localStorage.getItem('access_token');

            if (apiService) {
                const resp = await apiService.get(url, {
                    responseType: 'arraybuffer',
                    withCredentials: useCredentials,
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        Accept: 'application/pdf'
                    }
                });
                if (!resp.data || resp.data.byteLength === 0) {
                    console.error("PDF response is empty");
                    return null;
                }
                return new Uint8Array(resp.data);
            }

            const resp = await fetch(url, {
                credentials: useCredentials ? 'include' : 'omit',
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    Accept: 'application/pdf'
                }
            });

            if (!resp.ok) {
                console.error(`Fetch failed with status ${resp.status}`);
                return null;
            }

            const buffer = await resp.arrayBuffer();
            if (!buffer || buffer.byteLength === 0) {
                console.error("PDF buffer is empty");
                return null;
            }
            return new Uint8Array(buffer);
        } catch (err) {
            console.error("Fallback fetch PDF failed:", err);
            return null;
        }
    };

    const renderPDF = async () => {
        try {
            // Validaciones previas
            const resolvedUrl = resolvePdfUrl(pdfData.value.url);
            if (!resolvedUrl) {
                notify && notify("No se pudo resolver la URL del PDF", "danger");
                return;
            }

            if (!pdfCanvas.value) {
                return;
            }

            const useCredentials = shouldSendCredentials(resolvedUrl);

            // Cancelar TODAS las tareas anteriores
            if (currentRenderTask) {
                currentRenderTask.destroy();
                currentRenderTask = null;
            }

            if (currentPageRenderTask) {
                currentPageRenderTask.cancel();
                currentPageRenderTask = null;
            }

            // *** PREVENCIÓN ROBUSTA DE RENDERIZADOS CONCURRENTES ***
            if (isRendering.value) {
                return;
            }

            // Marcar como renderizando ANTES de cualquier operación async
            isRendering.value = true;

            await nextTick();
            const token = localStorage.getItem('access_token');
            let pdf = null;
            let loadingTask = null;
            try {
                loadingTask = getDocument({
                    url: resolvedUrl,
                    // Permite cookies si el PDF está protegido por sesión (Laravel/Sanctum)
                    withCredentials: useCredentials,
                    // Adjunta token si el PDF requiere autorización (mismo esquema que axios)
                    httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined
                });
                currentRenderTask = loadingTask;
                pdf = await loadingTask.promise;
            } catch (primaryError) {
                console.warn("Carga directa de PDF falló, intentando fallback con fetch", primaryError);
                const data = await fetchPdfAsUint8(resolvedUrl, useCredentials);
                if (!data) {
                    const missingError = new Error(`Missing PDF "${resolvedUrl}"`);
                    missingError.name = 'MissingPDFException';
                    throw missingError;
                }
                loadingTask = getDocument({ data });
                currentRenderTask = loadingTask;
                pdf = await loadingTask.promise;
            }

            const page = await pdf.getPage(1);

            // Verificar que todavía somos la tarea activa (podríamos haber sido cancelados)
            if (currentRenderTask !== loadingTask) {
                return;
            }

            // Obtener el canvas y context DESPUÉS de todas las operaciones async
            const canvas = pdfCanvas.value;
            if (!canvas) {
                return;
            }

            const context = canvas.getContext("2d");

            // Obtener viewport original para calcular proporciones reales
            const originalViewport = page.getViewport({ scale: 1 });

            // Dimensiones exactas basadas en el PDF real: 2550 x 3300 puntos
            // Proporción real del PDF: 0.7727 (ancho/alto)

            // Dimensiones optimizadas para el canvas (mantienen proporción exacta)
            const canvasHeight = 1115;
            const canvasWidth = 861; // Calculado: Math.round(1115 * 0.7727)

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            // Limpiar el canvas
            context.clearRect(0, 0, canvas.width, canvas.height);

            // Escalar para que el PDF llene exactamente el canvas en altura
            // manteniendo proporciones de formato Letter
            const scale = canvasHeight / originalViewport.height;
            const viewport = page.getViewport({ scale: scale });

            // Centrar horizontalmente si el PDF escalado es más estrecho que el canvas
            const offsetX = (canvasWidth - viewport.width) / 2;
            const offsetY = 0; // Sin offset vertical

            context.save();
            context.translate(offsetX, offsetY);

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            // *** RENDERIZADO CON CONTROL DE CONCURRENCIA ***
            const pageRenderTask = page.render(renderContext);
            currentPageRenderTask = pageRenderTask;

            // Verificar que somos la tarea activa antes del renderizado final
            if (currentRenderTask !== loadingTask) {
                console.log("🛑 Tarea cancelada antes del renderizado final");
                pageRenderTask.cancel();
                context.restore();
                return;
            }

            await pageRenderTask.promise;
            context.restore();

        } catch (error) {
            console.error("Error al renderizar el PDF:", error);
            if (error.name === 'RenderingCancelledException') {
                console.log("🛑 Renderizado cancelado correctamente");
            } else if (error.name === 'InvalidPDFException') {
                notify && notify("El archivo PDF no es válido o está corrupto", "danger");
            } else if (error.name === 'MissingPDFException') {
                notify && notify("No se pudo cargar el archivo PDF", "danger");
            } else if (error.name === 'UnexpectedResponseException') {
                notify && notify("Error de red al cargar el PDF", "danger");
            } else if (error.message && error.message.includes('canvas during multiple render')) {
                notify && notify("Error de renderizado concurrente, reintentando...", "warning");
                // Reintentar después de un breve delay
                setTimeout(() => {
                    if (!isRendering.value) {
                        renderPDF();
                    }
                }, 300);
            } else {
                notify && notify("Error al renderizar el PDF", "danger");
            }
        } finally {
            // Limpiar TODAS las referencias y liberar el flag de renderizado
            currentRenderTask = null;
            currentPageRenderTask = null;
            isRendering.value = false;
        }
    };

    /**
     * --------------------------------------------------------------------------------
     * 5. Mover las etiquetas en la página (drag & drop)
     * --------------------------------------------------------------------------------
     */
    const moveLabel = (event, label) => {
        // Obtener el contenedor del PDF para calcular las coordenadas relativas
        const pdfContainerRect = pdfContainer.value.getBoundingClientRect();
        const scale = visualScale.value;

        // Calcular la posición inicial relativa al contenedor escalado
        const startX = (event.clientX - pdfContainerRect.left) / scale - label.x;
        const startY = (event.clientY - pdfContainerRect.top) / scale - label.y;

        const snapThreshold = 10; // Umbral para alinear (en píxeles del canvas original)
        const onMouseMove = (moveEvent) => {
            // Calcular la nueva posición relativa al contenedor escalado
            const newX = (moveEvent.clientX - pdfContainerRect.left) / scale - startX;
            const newY = (moveEvent.clientY - pdfContainerRect.top) / scale - startY;

            // Asegurar que la etiqueta no salga de los límites del canvas original
            label.x = Math.max(0, Math.min(newX, containerWidth.value - (label.width || 100)));
            label.y = Math.max(0, Math.min(newY, containerHeight.value - (label.height || 20)));

            // Alineación con otras etiquetas (usando coordenadas del canvas original)
            pdfData.value.labels.forEach((other) => {
                if (other.id !== label.id) {
                    // Alineación vertical (eje Y)
                    if (Math.abs(label.y - other.y) < snapThreshold) {
                        label.y = other.y;
                    }

                    // Alineación horizontal (eje X)
                    if (Math.abs(label.x - other.x) < snapThreshold) {
                        label.x = other.x;
                    }
                }
            });
        };

        const onMouseUp = () => {
            console.log(`🎯 Etiqueta ${label.id} movida a posición:`, { x: label.x, y: label.y });
            updateSize(label);
            savePdfData();
            console.log("💾 Posición guardada en localStorage");
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const updateSize = (label) => {
        nextTick(() => {
            const textarea = document.querySelector(`div#${label.id}`);
            if (textarea) {
                label.width = textarea.offsetWidth;
                label.height = textarea.offsetHeight;
                savePdfData();
            }
        });
    };

    /**
     * --------------------------------------------------------------------------------
     * 6. Guardar la configuración en el backend
     * --------------------------------------------------------------------------------
     */
    const handleSave = async () => {
        const result = await Swal.fire({
            title: "Guardar cambios",
            text: "Se sobreescribirá la configuración en el backend.",
            icon: "info",
            showCancelButton: true,
            confirmButtonText: "Sí, guardar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            let labelsToProcess = pdfData.value.labels.map((label) => {
                // Si es un campo editable, obtenemos el contenido HTML actual
                if (label.isInput) {
                    const element = document.querySelector(`div#${label.id}`);
                    if (element) {
                        label.value = element.innerHTML;
                    }
                }

                // Formato exacto requerido por el backend: x, y, id, text, value, width, height, isInput
                return {
                    x: label.x,
                    y: label.y,
                    id: label.id,
                    text: label.text || "",
                    value: label.value || "",
                    width: label.width,
                    height: label.height,
                    isInput: label.isInput || false
                };
            });

            // Aplicar filtro si está definido
            if (filterLabelsForSave && typeof filterLabelsForSave === 'function') {
                labelsToProcess = filterLabelsForSave(labelsToProcess);
                console.log("Etiquetas filtradas para PDF:", labelsToProcess);
            }

            // ✅ CORREGIDO: Enviar como objeto, no como string
            // El backend y axios lo convertirán automáticamente a JSON
            const labelsData = { labels: labelsToProcess };

            const payload = {
                id: resourceId,
                [pdfParametersKey]: labelsData, // ✅ Objeto, NO string
            };

            console.log("💾 Guardando en servidor:");
            console.log("   - Payload completo:", payload);
            console.log("   - Labels data (objeto):", labelsData);
            console.log("   - Tipo:", typeof labelsData);
            console.log("   - Labels procesados:", labelsToProcess);
            const response = await editModel(payload);
            if (response && (response.status === 200 || response.status === 201)) {
                console.log("✅ Guardado exitoso en servidor");

                // IMPORTANTE: Actualizar localStorage con los datos que se guardaron en el servidor
                // Esto asegura que localStorage esté sincronizado con la BD
                console.log("🔄 Actualizando localStorage con datos del servidor");
                savePdfData();

                // Si tenemos updateUserStore, actualizar el store también
                if (updateUserStore && userData?.value?.doctor) {
                    const updatedDoctor = {
                        ...userData.value.doctor,
                        [pdfParametersKey]: { labels: labelsToProcess } // ✅ Objeto, NO string
                    };
                    console.log("🔄 Actualizando store de usuario");
                    updateUserStore({
                        ...userData.value,
                        doctor: updatedDoctor
                    });
                }

                Swal.fire({
                    title: "Cambios guardados",
                    text: response.data.message,
                    icon: "success",
                    confirmButtonText: "Cerrar",
                });
            } else {
                console.error("❌ Error al guardar en servidor:", response);
            }
        }
    };

    /**
     * --------------------------------------------------------------------------------
     * 7. Generar PDF final (opcional)
     * --------------------------------------------------------------------------------
     */
    const generatePDF = async (somePayload = {}) => {
        if (!generatePdfMethod) {
            console.warn("No se definió generatePdfMethod en el composable. Retornando...");
            return;
        }

        // Aplicar filtro a las etiquetas antes de generar el PDF
        if (filterLabelsForSave && typeof filterLabelsForSave === 'function') {
            // Actualizar los valores de los campos editables antes de filtrar
            pdfData.value.labels.forEach((label) => {
                if (label.isInput) {
                    const element = document.querySelector(`div#${label.id}`);
                    if (element) {
                        label.value = element.innerHTML;
                    }
                }
            });

            // Crear una copia filtrada de las etiquetas para el PDF
            const filteredLabels = filterLabelsForSave(pdfData.value.labels);

            // Guardar temporalmente las etiquetas originales
            const originalLabels = [...pdfData.value.labels];

            // Reemplazar temporalmente con las etiquetas filtradas
            pdfData.value.labels = filteredLabels;

            try {
                const response = await generatePdfMethod(somePayload);
                if (response && response.status === 200) {
                    window.open(response.data.data.pdf_url, "_blank");
                    notify(response.data.message, "", "success");
                }
            } finally {
                // Restaurar las etiquetas originales
                pdfData.value.labels = originalLabels;
            }
        } else {
            try {
                const response = await generatePdfMethod(somePayload);
                if (response && response.status === 200) {
                    window.open(response.data.data.pdf_url, "_blank");
                    notify(response.data.message, "", "success");
                }
            } catch (error) {
                notify("Ocurrió un error desconocido al generar el PDF.", "", "danger");
            }
        }
    };

    /**
     * --------------------------------------------------------------------------------
     * 8. Inicializar en onMounted
     * --------------------------------------------------------------------------------
     */
    onMounted(async () => {
        // Inicializar pdfData.labels si no existe
        if (!pdfData.value.labels) {
            pdfData.value.labels = [];
        }

        // 1) Primero obtiene el recurso de la API (datos del servidor tienen prioridad)
        await getResource();

        // 2) Solo si no hay datos del servidor, carga localStorage como fallback
        if (pdfData.value.labels.length === 0) {
            loadPdfData();
        }

        // 3) Si no hay etiquetas después de cargar todo, el servidor debe proporcionarlas
        if (pdfData.value.labels.length === 0) {
            console.warn('⚠️ No hay labels disponibles. El backend debe proporcionar prescription_pdf_parameters.');
        }

        // 4) Forzamos un render tras el fetch
        await nextTick();

        // 5) Intentar renderizar el PDF si tenemos URL pero aún no se ha renderizado
        // MEJORADO: Asegurar que el canvas esté listo con retry
        if (pdfData.value.url && pdfCanvas.value) {
            let renderAttempts = 0;
            const maxAttempts = 5;

            const attemptRender = async () => {
                if (pdfCanvas.value && pdfCanvas.value.getContext) {
                    await renderPDF();
                    return true;
                } else if (renderAttempts < maxAttempts) {
                    renderAttempts++;
                    await new Promise(resolve => setTimeout(resolve, 100));
                    return await attemptRender();
                }
                return false;
            };

            await attemptRender();
        }

        // 6) Configurar escala visual inicial
        await nextTick(); // Asegurar que el DOM esté listo
        setTimeout(() => {
            updateVisualScale();
        }, 100); // Pequeño delay para asegurar que el DOM esté completamente listo

        // 7) Agregar listener para cambios de tamaño de ventana
        window.addEventListener('resize', updateVisualScale);
    });

    // Limpiar listeners cuando se desmonte el componente
    onBeforeUnmount(() => {
        window.removeEventListener('resize', updateVisualScale);
    });

    /**
     * --------------------------------------------------------------------------------
     * 9. Watchers para reaccionar a cambios
     * --------------------------------------------------------------------------------
     */
    watch(
        () => pdfData.value.labels,
        (newLabels) => {
            if (Array.isArray(newLabels)) {
                newLabels.forEach((label) => {
                    if (label.isInput) {
                        const textarea = document.querySelector(`div#${label.id}`);
                        if (textarea) {
                            label.width = textarea.offsetWidth;
                            label.height = textarea.offsetHeight;
                        }
                    }
                });
                savePdfData();
            }
        },
        { deep: true }
    );

    // Watcher para la URL del PDF - renderizar automáticamente cuando cambie
    watch(
        () => pdfData.value.url,
        async (newUrl, oldUrl) => {
            if (newUrl && newUrl !== oldUrl && pdfCanvas.value) {
                await nextTick(); // Asegurar que el DOM esté actualizado
                await renderPDF();
                // Actualizar escala después del renderizado
                await nextTick();
                setTimeout(() => {
                    updateVisualScale();
                }, 150); // Delay para asegurar que el renderizado esté completo
            }
        },
        { immediate: false }
    );

    // Watcher para userData - por si los datos se cargan después del montado
    if (userData) {
        watch(
            () => userData.value,
            async (newUserData, oldUserData) => {

                // Si tenemos nuevos datos de usuario con doctor, y antes no los teníamos
                if (newUserData?.doctor && (!oldUserData?.doctor || oldUserData?.doctor !== newUserData?.doctor)) {
                    await getResource();
                }
            },
            { deep: true, immediate: false }
        );
    }

    /**
     * --------------------------------------------------------------------------------
     * 10. Funciones para manejo de escala visual
     * --------------------------------------------------------------------------------
     */
    const updateVisualScale = () => {
        if (!pdfContainer.value) return;

        let availableWidth, availableHeight;

        // Buscar el contenedor padre más apropiado (.pdf-viewer-container)
        let containerElement = pdfContainer.value.parentElement;

        // Si tenemos el contenedor específico, usarlo
        if (containerElement && containerElement.classList.contains('pdf-viewer-container')) {
            // Obtener las dimensiones disponibles del contenedor
            const containerRect = containerElement.getBoundingClientRect();
            availableWidth = containerRect.width - 40; // Padding interno
            availableHeight = Math.max(containerRect.height - 40, window.innerHeight * 0.6);
        } else {
            // Fallback: usar dimensiones de ventana
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Calcular espacio disponible considerando márgenes y otros elementos
            availableWidth = Math.min(viewportWidth * 0.9, viewportWidth - 100);
            availableHeight = viewportHeight * 0.7;
        }

        // Calcular escalas para ancho y alto
        const scaleX = availableWidth / containerWidth.value;
        const scaleY = availableHeight / containerHeight.value;

        // Usar la escala más pequeña para mantener proporciones, pero limitar entre 0.3 y 1
        const newScale = Math.max(0.3, Math.min(scaleX, scaleY, 1));

        // Solo actualizar si hay un cambio significativo
        if (Math.abs(visualScale.value - newScale) > 0.02) {
            visualScale.value = newScale;
            applyVisualScale();
        }
    };

    const applyVisualScale = () => {
        if (!pdfContainer.value) return;

        const scale = visualScale.value;
        const scaledWidth = containerWidth.value * scale;
        const scaledHeight = containerHeight.value * scale;

        // Aplicar transformación CSS al contenedor del PDF
        pdfContainer.value.style.transform = `scale(${scale})`;
        pdfContainer.value.style.transformOrigin = 'center center';
        pdfContainer.value.style.width = `${containerWidth.value}px`;
        pdfContainer.value.style.height = `${containerHeight.value}px`;

        // Ajustar el contenedor padre (.pdf-viewer-container) para el espacio que realmente ocupa
        const parentElement = pdfContainer.value.parentElement;
        if (parentElement && parentElement.classList.contains('pdf-viewer-container')) {
            // El contenedor padre debe ajustarse al tamaño escalado y centrar el contenido
            parentElement.style.width = `${scaledWidth + 40}px`; // +40 para padding
            parentElement.style.height = `${scaledHeight + 40}px`; // +40 para padding
            parentElement.style.display = 'flex';
            parentElement.style.justifyContent = 'center';
            parentElement.style.alignItems = 'center';
            parentElement.style.minWidth = 'auto';
            parentElement.style.minHeight = `${Math.max(300, scaledHeight + 40)}px`;
            parentElement.style.margin = '0 auto';
            parentElement.style.textAlign = 'center';
        }

    };

    /**
     * --------------------------------------------------------------------------------
     * 11. Método de debug para forzar renderizado
     * --------------------------------------------------------------------------------
     */
    const forceRender = async () => {
        if (pdfData.value.url && pdfCanvas.value) {
            await renderPDF();
        } else {
            console.warn("⚠️ No se puede forzar renderizado: faltan URL o canvas");
        }
    };

    /**
     * Cancela el renderizado actual si está en progreso
     */
    const cancelCurrentRender = () => {
        if (currentRenderTask) {
            currentRenderTask.destroy();
            currentRenderTask = null;
        }

        if (currentPageRenderTask) {
            currentPageRenderTask.cancel();
            currentPageRenderTask = null;
        }

        isRendering.value = false;
    };

    // Retornamos todo lo que necesites en tu componente
    return {
        pdfContainer,
        pdfCanvas,
        pdfData,
        dataEntity,
        selectedFile,
        isRendering, // Estado del renderizado

        // Variables de escala visual
        visualScale,
        containerWidth,
        containerHeight,

        // Métodos
        getResource,
        loadPdfData,
        savePdfData,
        loadDefaultPdfData,
        handleDrop,
        uploadPDF,
        sendPdfUpload,
        renderPDF,
        moveLabel,
        updateSize,
        handleSave,
        generatePDF,
        forceRender, // Método de debug
        cancelCurrentRender, // Método para cancelar renderizado
        updateVisualScale, // Método para actualizar escala visual
        applyVisualScale, // Método para aplicar escala visual
    };
}
